import React from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Loader2, Check, X } from "lucide-react";
import { cn } from "utils/cn";
import type { GuideView } from "hooks/useGuideCoordinator";
import type { GuidedCopy, GuidedFlowDefinition } from "pages/TechPackPage/utils/techpackCreateFlow";
import type { GuidanceStep } from "pages/TechPackPage/utils/guidanceReport";

/**
 * The guided-workflow surface (REF-6217 T1.4) — the guide card inside the Ask Refabric panel, and
 * the collapsed pill shown when the panel is closed.
 *
 * **Two renderings of one `GuideView`.** Neither holds state: the step, its readiness, the blocked
 * reason and the notice all arrive already decided (feature → report → coordinator), and every
 * string comes from the flow definition's authored copy keys. No Tech Pack state is imported here,
 * there is no gate, no cursor, and nothing that can advance the wizard — the card describes; the
 * product's own controls act.
 */

interface StepPresentation {
  copy: GuidedCopy;
  /** Position among the *navigable* steps, or null for an application state. */
  position: { index: number; total: number } | null;
  optional: boolean;
}

/**
 * Everything both renderings need about a reported step.
 *
 * The early return is what keeps `extracting` and `done` out of the numbering: they are application
 * states, not steps the user walks through, so they get their own copy and no position — which is
 * why the card can never show "5 of 4" while the tech pack is being built.
 */
export const stepPresentation = (definition: GuidedFlowDefinition, step: GuidanceStep): StepPresentation => {
  if (step === "extracting" || step === "done") return { copy: definition.appStates[step], position: null, optional: false };

  const copy = definition.steps[step];
  const index = definition.stepOrder.indexOf(step);

  // `index < 0` is unreachable while the definition's order covers its own steps (pinned in
  // `techpackCreateFlow.test.ts`) — but rendering "0/4" would be worse than rendering nothing.
  return { copy, position: index < 0 ? null : { index: index + 1, total: definition.stepOrder.length }, optional: copy.optional };
};

const CARD = "rounded-lg border border-black-300 bg-black-200 px-3 py-2.5 flex flex-col gap-1.5 mx-4 mt-3";
const TITLE = "text-[11px] uppercase tracking-wide text-purpleGreen";
const NAME = "text-[13px] font-semibold text-white flex items-center gap-1.5";
const BODY = "text-[12px] leading-relaxed text-grey";
const ICON_BUTTON = "h-6 w-6 shrink-0 flex items-center justify-center rounded-md text-grey hover:bg-black-300 hover:text-white transition-all";

interface Props {
  guide: GuideView;
  /** Navigate back to the flow's entry. The caller owns the unsaved-changes guard. */
  onReturn: (entry: string) => void;
  /** The coordinator's exit — it owns whether that counts as an exit for analytics. */
  onExit: () => void;
}

export const GuideCard = ({ guide, onReturn, onExit }: Props) => {
  const { t } = useTranslation();
  if (guide.status === "inactive") return null;

  const { definition } = guide;
  const title = t(definition.titleKey);

  // The user is off the flow's page: the run is still live, so this is an invitation back, not an
  // ending. Nothing here restarts the guide.
  if (guide.status === "away") {
    return (
      <div className={CARD} data-testid="guide-card">
        <span className={TITLE}>{title}</span>
        <button
          onClick={() => onReturn(definition.entry)}
          data-testid="guide-return"
          className="flex w-fit items-center gap-2 rounded-lg border border-purpleGreen/40 bg-purpleGreen/10 px-3 py-1.5 text-[12px] text-white transition-all hover:bg-purpleGreen/20"
        >
          <span>{t(definition.awayKey)}</span>
          <ArrowRight size={13} className="shrink-0" />
        </button>
      </div>
    );
  }

  if (guide.status === "completed") {
    const copy = definition.appStates.done;

    return (
      <div className={CARD} data-testid="guide-card">
        <div className="flex items-start justify-between gap-2">
          <span className={TITLE}>{title}</span>
          <button onClick={onExit} data-testid="guide-dismiss" className={ICON_BUTTON} title={t("chatbot.dismiss")} aria-label={t("chatbot.dismiss")}>
            <X size={13} />
          </button>
        </div>
        <span className={NAME} data-testid="guide-step-name">
          <Check size={13} className="shrink-0 text-purpleGreen" />
          {t(copy.nameKey)}
        </span>
        <p className={BODY} data-testid="guide-instruction">
          {t(copy.instructionKey)}
        </p>
      </div>
    );
  }

  const { report } = guide;
  const { copy, position, optional } = stepPresentation(definition, report.step);

  return (
    <div className={CARD} data-testid="guide-card">
      <div className="flex items-start justify-between gap-2">
        <span className={TITLE}>{title}</span>
        <button onClick={onExit} data-testid="guide-exit" className={ICON_BUTTON} title={t("chatbot.guide.exit")} aria-label={t("chatbot.guide.exit")}>
          <X size={13} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={NAME} data-testid="guide-step-name">
          {report.step === "extracting" && <Loader2 size={13} className="shrink-0 animate-spin text-purpleGreen" />}
          {t(copy.nameKey)}
        </span>
        {position && (
          <span className="text-[11px] text-grey tabular-nums" data-testid="guide-progress">
            {position.index}/{position.total}
          </span>
        )}
        {optional && (
          <span className="rounded-full border border-black-300 px-1.5 py-0.5 text-[10px] text-grey" data-testid="guide-optional">
            {t("chatbot.guide.optional")}
          </span>
        )}
      </div>

      <p className={BODY} data-testid="guide-instruction">
        {t(copy.instructionKey)}
      </p>

      {/* A step the app itself is blocking. `stepReady` and the reason are both the feature's
          answer — the card only explains it, so it can never claim a step is passable. */}
      {!report.stepReady && report.blockedReason && (
        <p className="text-[12px] leading-relaxed text-white" data-testid="guide-blocked">
          {t(definition.blockedKeys[report.blockedReason])}
        </p>
      )}

      {/* How the last extraction attempt ended. Not a step, and not a retry: the copy points at the
          product's own Create button, which stays the only thing that can start another attempt. */}
      {report.notice && (
        <p className="text-[12px] leading-relaxed text-orange" data-testid="guide-notice">
          {t(definition.noticeKeys[report.notice])}
        </p>
      )}
    </div>
  );
};

/**
 * The collapsed presence. The panel defaults closed, so without this an active guide would be
 * invisible until someone happened to open Ask Refabric.
 *
 * Same `GuideView`, one line of it — no second lifecycle, no state of its own. `right` comes from
 * the measured top-bar clearance rather than a px offset, so it sits beside the Ask Agent cluster in
 * every locale and falls back to the corner where that bar isn't rendered (CLAUDE.md Trap #13).
 */
export const GuideChip = ({ guide, isMobile, onOpen }: { guide: GuideView; isMobile: boolean; onOpen: () => void }) => {
  const { t } = useTranslation();
  if (guide.status === "inactive") return null;

  const { definition } = guide;
  const presentation = guide.status === "active" ? stepPresentation(definition, guide.report.step) : null;
  const label = guide.status === "away" ? t(definition.awayKey) : presentation ? t(presentation.copy.nameKey) : t(definition.appStates.done.nameKey);

  return (
    <button
      onClick={onOpen}
      data-testid="guide-chip"
      aria-label={t("chatbot.open_aria")}
      style={{ right: isMobile ? "1rem" : "var(--dashboard-topbar-clearance, 2rem)" }}
      className={cn(
        "fixed z-30 flex max-w-[70vw] items-center gap-2 rounded-full border border-purpleGreen/40 bg-black-200 px-3 py-1.5 shadow-lg transition-all hover:bg-black-300",
        // `top-[6px]` is the offset the other clearance-var consumers already use to sit in the top
        // bar's band (`ReportPage.tsx`); mobile clears the 52px MobileTopBar instead.
        isMobile ? "top-[60px]" : "top-[6px]"
      )}
    >
      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-purpleGreen" />
      <span className="truncate text-[12px] text-white">{label}</span>
      {presentation?.position && (
        <span className="shrink-0 text-[11px] text-grey tabular-nums" data-testid="guide-chip-progress">
          {presentation.position.index}/{presentation.position.total}
        </span>
      )}
    </button>
  );
};
